import React from "react"
import {
  ListGroup,
  ListGroupItem,
  Container,
  Alert,
  Nav,
  NavItem,
  NavLink,
} from "reactstrap"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Trans, useLingui } from "@lingui/react"
import PropTypes from "prop-types"
import { selectCreatedVslaInfo } from "../../store/reducers/getVslas"
import { connect } from "react-redux"
import { setCurrentPage } from "../../store/reducers/navigation"
import { selectCycle, selectPreviousMeeting } from "../../store/actions/meetings"
import { fullArabicDateConverter } from "../../view/shared/utilities/arabicDateConverter"
import classnames from "classnames"

const iconStyle = {
  fontSize: "20px",
  float: "right",
}

const headerStyle = {
  color: "#E36F1E",
  fontFamily: "quicksand",
  fontWeight: "bolder",
  fontSize: "1.8em",
  textAlign: "center",
  paddingBottom: "0.5em",
  margin: "34px 0 0 0",
}

const Meetings = (props) => {
  const { i18n } = useLingui()
  const [data, setData] = React.useState([])
  const extractData = () => {
    let key = props.cycle ? props.cycle.id : "active"
    if (props.activeTab === 1) {
      key = "active"
    }
    setData(props.cycleMeetings[key] || [])
  }
  React.useEffect(() => {
    extractData()
  }, [])

  React.useEffect(() => {
    extractData()
  }, [props.cycleMeetings])

  return (
    <Container>
      {data.length === 0 ? (
        <Alert color="warning">
          <span style={{ fontSize: "16px", fontFamily: "Fira Sans" }}>
            <Trans id="no.previous.meetings">No previous meetings</Trans>
          </span>
        </Alert>
      ) : (
        ""
      )}
      {data.map((prev_meeting) => (
        <ListGroup flush key={prev_meeting.id}>
          <span
            key={prev_meeting.id}
            onClick={() => {
              props.setPage("group-previousMeetingsItem")
              props.setPreviousMeeting(prev_meeting)
            }}
          >
            <ListGroupItem
              className="textStyle"
              style={{
                textAlign: "center",
                fontFamily: "Fira Sans",
                fontSize: "20px",
              }}
            >
              {!["ar"].includes(i18n.locale) &&
                new Date(prev_meeting.date).toLocaleString(i18n.language, {
                  dateStyle: "full",
                })}
              {["ar"].includes(i18n.locale) &&
                fullArabicDateConverter(prev_meeting.date)}
              {prev_meeting && prev_meeting.suffix && `(${prev_meeting.suffix})`}
              <FontAwesomeIcon style={iconStyle} icon="angle-right" />
              <small style={{ display: "block", textAlign: "center", color: prev_meeting.syncStatus === 'error' ? "red" : "#E36F1E", fontStyle: "italic", fontSize: "14px" }}>
                {prev_meeting.is_offline ? 'Pending sync' : prev_meeting.syncStatus === 'error' ? 'sync error' : ''}
              </small>
            </ListGroupItem>
          </span>
        </ListGroup>
      ))}
    </Container>
  )
}

const Cycles = (props) => {
  return (
    <Container>
      {props.cycles
        .filter((cycle) => !cycle.isActive)
        .map((cycle) => (
          <ListGroup flush key={cycle.id}>
            <span
              onClick={() => {
                props.setCycle(cycle)
              }}
            >
              <ListGroupItem
                key={cycle.id}
                className="textStyle"
                style={{
                  textAlign: "center",
                  fontFamily: "Fira Sans",
                  fontSize: "20px",
                }}
              >
                {cycle.name}
                <FontAwesomeIcon style={iconStyle} icon="angle-right" />
              </ListGroupItem>
            </span>
          </ListGroup>
        ))}
    </Container>
  )
}

const PreviousMeetings = (props) => {
  const { i18n } = useLingui()
  const [state, setState] = React.useState({
    isLoaded: false,
    previous_meetings: [],
    lang: i18n.locale,
  })

  const [cycleMeetings, setMeetings] = React.useState({})
  React.useEffect(() => {
    let activeCycle
    for (const item of props.cycles) {
      if (item.isActive) {
        activeCycle = item
        break
      }
    }
    const { group, vsla } = props.vslaSummary[0]
    setState({ ...state, group_id: group?.id, vsla_id: vsla?.id })
    const mapper = {
      active: [],
    }
    const suffixMap = new Map()
    props.previousMeetingsTransactions.forEach((item) => {
      if (!item.cycle_id) {
        return
      } else if (activeCycle && item.cycle_id === activeCycle.id) {
        const meetings = [...(mapper["active"] || []), item]
        mapper["active"] = meetings
      } else {
        const meetings = [...(mapper[item.cycle_id] || []), item]
        mapper[item.cycle_id] = meetings
      }
    })
    // Sort meetings in the group by date
    const sortedMeetings = [...mapper.active].sort((a, b) => {
      const dateA = new Date(a.date)
      const dateB = new Date(b.date)
      // Compare the dates to sort in ascending order
      return dateA - dateB
    })
    sortedMeetings.forEach((item) => {
      const date = new Date(item.date).toISOString().split("T")[0]
      // Create or increment a suffix for the date
      suffixMap.set(date, (suffixMap.get(date) || 0) + 1)
      item.suffix = suffixMap.get(date)
    })
    setMeetings(mapper)
    if (props.selectedCycle) {
      toggle(2)
    }
  }, [props.previousMeetingsTransactions])

  const [activeTab, setTab] = React.useState(1)
  const toggle = (tab) => {
    if (activeTab !== tab) {
      setTab(tab)
      if (tab === 1) {
        props.setCycle(null)
      }
    }
  }

  const map = {
    1: connect(mapStateToProps, mapDispatchToProps)(Meetings),
    2: connect(mapStateToProps, mapDispatchToProps)(Cycles),
  }
  const titles = { 1: "prev_meeting", 2: "cycles_title" }
  const [DynamicComponent, setComponent] = React.useState(map[activeTab])
  React.useEffect(() => {
    if (!props.selectedCycle) {
      setComponent(map[activeTab])
      return
    }
    setComponent(map[1])
  }, [activeTab])

  React.useEffect(() => {
    if (!props.selectedCycle && activeTab === 2) {
      setComponent(map[2])
      return
    }
    setComponent(map[1])
  }, [props.selectedCycle])

  return (
    <div className="tab-div-wrapper">
      <Nav tabs>
        <NavItem>
          <NavLink
            className={classnames({
              active: activeTab === 1,
              "active-tab": activeTab === 1,
              "previous-meeting-tabs": true,
            })}
            onClick={() => {
              toggle(1)
            }}
          >
            <Trans id="current.cycle">Current Cycle</Trans>
          </NavLink>
        </NavItem>
        <NavItem>
          <NavLink
            className={classnames({
              active: activeTab === 2,
              "active-tab": activeTab === 2,
              "previous-meeting-tabs": true,
            })}
            onClick={() => {
              toggle(2)
            }}
          >
            <Trans id="previous.cycles">Previous Cycles</Trans>
          </NavLink>
        </NavItem>
      </Nav>
      <div>
        <h4 style={headerStyle}>
          {!props.selectedCycle
            ? titles[activeTab] === "prev_meeting"
              ? i18n._("Previous Meetings")
              : i18n._("Cycles")
            : props.selectedCycle.name}
        </h4>
      </div>
      {
        <DynamicComponent
          cycle={props.selectedCycle}
          activeTab={activeTab}
          cycleMeetings={cycleMeetings}
          previousMeetingsTransactions={props.previousMeetingsTransactions}
        />
      }
    </div>
  )
}

Meetings.propTypes = {
  cycle: PropTypes.object,
  activeTab: PropTypes.number,
  cycleMeetings: PropTypes.object,
  setPage: PropTypes.func,
  setPreviousMeeting: PropTypes.func,
}

Cycles.propTypes = {
  cycles: PropTypes.array,
  setCycle: PropTypes.func,
}

PreviousMeetings.propTypes = {
  cycles: PropTypes.array,
  vslaSummary: PropTypes.array,
  previousMeetingsTransactions: PropTypes.array,
  selectedCycle: PropTypes.object,
  setCycle: PropTypes.func,
}

const mapStateToProps = (state) => ({
  cycles: state.cycles.data,
  selectedCycle: state.cycles.selectedCycle,
  vslaSummary: selectCreatedVslaInfo(state),
  previousMeetingsTransactions: state.previousMeetingsTransactions.meetings,
})

const mapDispatchToProps = (dispatch) => ({
  setCycle: (cycle) => dispatch(selectCycle(cycle)),
  setPreviousMeeting: (item) => dispatch(selectPreviousMeeting(item)),
  setPage: (page) => dispatch(setCurrentPage(page)),
})
export default connect(mapStateToProps, mapDispatchToProps)(PreviousMeetings)
